import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import type { ObjectiveWithProgress } from '@/types/objective'

import { fetchObjectives } from '../api'
import { buildGoals, isGoalCompleted } from '../model/milestones'

export const useObjectives = () => {
  const { digitalProductId } = useDigitalProductContext()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['objectives', digitalProductId],
    queryFn: () => fetchObjectives(digitalProductId!),
    enabled: !!digitalProductId,
    refetchInterval: 60_000,
    staleTime: 30_000
  })

  const objectives = data ?? []

  // Meta = escada de marcos ou objetivo solto. O denominador conta metas: com
  // uma escada de 15 degraus, contar objetivos travava o evento em percentual
  // baixíssimo e anunciava "faltam 14 objetivos" quando faltava uma coisa só.
  const goals = useMemo(() => buildGoals(objectives), [objectives])

  const completedCount = useMemo(
    () => goals.filter(isGoalCompleted).length,
    [goals]
  )

  const totalCount = goals.length

  const overallPercent = useMemo(
    () => (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0),
    [completedCount, totalCount]
  )

  // Continua somando todos os objetivos, agrupados ou não: é dinheiro real, e
  // agrupar não muda quanto o participante pode ganhar.
  const totalPossibleCoins = useMemo(
    () => objectives.reduce((acc, o) => acc + (o.coin_reward ?? 0), 0),
    [objectives]
  )

  const earnedCoins = useMemo(
    () =>
      objectives.reduce(
        (acc, objective) => acc + (objective.completed ? (objective.coin_reward ?? 0) : 0),
        0
      ),
    [objectives]
  )

  return {
    objectives,
    goals,
    isLoading,
    isError,
    refetch,
    completedCount,
    totalCount,
    overallPercent,
    totalPossibleCoins,
    earnedCoins
  }
}
